import { ref } from 'vue'
import { usePlayerStore } from '@/stores/player'
import {
	signalingCreate,
	signalingJoin,
	signalingPoll,
	signalingAddSlot,
	type CreateRoomRequest,
	type JoinRoomRequest,
} from '@/lib/signaling-api'

export type AnswerCallback = (
	slotId: string,
	answer: string,
	playerId: string
) => void | Promise<void>

export function useSignaling() {
	const playerStore = usePlayerStore()
	const isPolling = ref(false)
	const pollError = ref<string | null>(null)

	const pollingIntervals = new Map<string, ReturnType<typeof setInterval>>()
	const processedAnswers = new Set<string>()

	const createRoomWithServer = async (
		roomId: string,
		slotId: string,
		offer: string,
		connectionMode: 'internet' | 'local'
	): Promise<boolean> => {
		const request: CreateRoomRequest = {
			roomId,
			slotId,
			offer,
			connectionMode,
			hostId: playerStore.myPlayerId || undefined,
		}

		const result = await signalingCreate(request)

		if (!result.success) {
			console.error('[useSignaling] Failed to create room:', result.error)
			pollError.value = result.error
			return false
		}

		console.log('[useSignaling] Room created on server:', result.data.roomId)
		return true
	}

	const joinRoomViaServer = async (
		roomId: string,
		slotId: string,
		answer: string,
		playerId: string
	): Promise<boolean> => {
		const request: JoinRoomRequest = {
			roomId,
			slotId,
			answer,
			playerId,
		}

		const result = await signalingJoin(request)

		if (!result.success) {
			console.error('[useSignaling] Failed to join room:', result.error)
			pollError.value = result.error
			return false
		}

		console.log('[useSignaling] Joined room via server:', roomId)
		return true
	}

	const addSlotToServer = async (
		roomId: string,
		slotId: string,
		offer: string
	): Promise<boolean> => {
		const result = await signalingAddSlot({ roomId, slotId, offer })

		if (!result.success) {
			console.error('[useSignaling] Failed to add slot:', result.error)
			pollError.value = result.error
			return false
		}

		console.log('[useSignaling] Added slot to server:', slotId)
		return true
	}

	const startPollingForAnswers = (
		roomId: string,
		onAnswer: AnswerCallback,
		intervalMs = 2000
	): void => {
		if (pollingIntervals.has(roomId)) {
			console.warn('[useSignaling] Already polling for room:', roomId)
			return
		}

		console.log('[useSignaling] Starting polling for room:', roomId, 'interval:', intervalMs)
		isPolling.value = true
		pollError.value = null

		const pollInterval = setInterval(async () => {
			const result = await signalingPoll(roomId)

			if (!result.success) {
				console.error('[useSignaling] Poll error:', result.error)
				pollError.value = result.error
				return
			}

			for (const answer of result.data.answers) {
				const answerKey = `${answer.slotId}:${answer.playerId}:${answer.timestamp}`

				if (processedAnswers.has(answerKey)) continue

				console.log('[useSignaling] Received answer for slot:', answer.slotId, 'player:', answer.playerId)
				processedAnswers.add(answerKey)

				try {
					await onAnswer(answer.slotId, answer.answer, answer.playerId)
				} catch (error) {
					console.error('[useSignaling] Error in answer callback:', error)
				}
			}

			pollError.value = null
		}, intervalMs)

		pollingIntervals.set(roomId, pollInterval)
	}

	const stopPolling = (roomId: string): void => {
		const interval = pollingIntervals.get(roomId)
		if (interval) {
			clearInterval(interval)
			pollingIntervals.delete(roomId)
			console.log('[useSignaling] Stopped polling for room:', roomId)
		}

		for (const key of processedAnswers) {
			if (key.startsWith(`${roomId}:`)) {
				processedAnswers.delete(key)
			}
		}

		isPolling.value = pollingIntervals.size > 0
	}

	const stopAllPolling = (): void => {
		for (const [roomId, interval] of pollingIntervals.entries()) {
			clearInterval(interval)
			console.log('[useSignaling] Stopped polling for room:', roomId)
		}

		pollingIntervals.clear()
		processedAnswers.clear()
		isPolling.value = false
	}

	return {
		isPolling,
		pollError,
		createRoomWithServer,
		joinRoomViaServer,
		addSlotToServer,
		startPollingForAnswers,
		stopPolling,
		stopAllPolling,
	}
}
